import { useMemo, useState } from "react";
import { ExternalLink, AlertCircle, MapPin, Beer, Users, Clock, ShoppingCart, Check } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const KEG_DIAGRAM = "https://customer-assets.emergentagent.com/job_trailhead-reserve/artifacts/ziy3qd8i_image.png";

// UTM-tagged referral URLs for traffic attribution
const UTM = "?utm_source=trailheadtaps.com&utm_medium=referral&utm_campaign=keg_guide";

const STORES = [
  {
    name: "BevMo!",
    tagline: "Kegs, bottles, and the full liquor wall",
    note: "Multiple Long Beach + OC locations",
    href: `https://www.bevmo.com/stores${UTM}`,
    mapsHref: "https://www.google.com/maps/search/?api=1&query=BevMo+Long+Beach+CA",
  },
  {
    name: "Total Wine & More",
    tagline: "Huge wine list + deep beer cellar",
    note: "Carson, Long Beach & Lakewood nearby",
    href: `https://www.totalwine.com/store-finder?searchTerm=Long%20Beach%2C%20CA${UTM.replace('?', '&')}`,
    mapsHref: "https://www.google.com/maps/search/?api=1&query=Total+Wine+Long+Beach+CA",
  },
  {
    name: "Costco",
    tagline: "Best price on macros — Bud, Modelo, Coors kegs",
    note: "Member-only · Signal Hill + Lakewood",
    href: `https://www.costco.com/beer.html${UTM}`,
    mapsHref: "https://www.google.com/maps/search/?api=1&query=Costco+Signal+Hill+CA",
  },
  {
    name: "Local LB Breweries",
    tagline: "Beachwood · Liberation · ISM · Steady Brewing",
    note: "Best for kegs of craft — we can call ahead for you",
    href: "https://www.google.com/maps/search/?api=1&query=Breweries+Long+Beach+CA",
    mapsHref: null,
  },
];

const KEGS = [
  { id: "half", name: "1/2 Barrel", pours: 165, priceLow: 180, priceHigh: 240 },
  { id: "quarter", name: "1/4 Barrel", pours: 82, priceLow: 110, priceHigh: 150 },
  { id: "sixth", name: "1/6 Barrel", pours: 55, priceLow: 80, priceHigh: 120 },
];

function buildCombo(drinksNeeded) {
  let rem = drinksNeeded;
  const half = Math.floor(rem / 165); rem -= half * 165;
  const quarter = Math.floor(rem / 82); rem -= quarter * 82;
  const sixth = rem > 0 ? Math.ceil(rem / 55) : 0;
  const items = [];
  if (half) items.push({ ...KEGS[0], qty: half });
  if (quarter) items.push({ ...KEGS[1], qty: quarter });
  if (sixth) items.push({ ...KEGS[2], qty: sixth });
  // Edge case: no recommendation at all (very tiny event) — recommend 1 x 1/6
  if (items.length === 0) items.push({ ...KEGS[2], qty: 1 });
  const totalPours = items.reduce((s, it) => s + it.qty * it.pours, 0);
  const low = items.reduce((s, it) => s + it.qty * it.priceLow, 0);
  const high = items.reduce((s, it) => s + it.qty * it.priceHigh, 0);
  return { items, totalPours, low, high };
}

function KegRecommender() {
  const [guests, setGuests] = useState(100);
  const [hours, setHours] = useState(5);
  const [buffer, setBuffer] = useState(true);

  const drinks = useMemo(() => {
    const raw = guests * 2 + guests * Math.max(0, hours - 1);
    return buffer ? Math.ceil(raw * 1.1) : raw;
  }, [guests, hours, buffer]);

  const combo = useMemo(() => buildCombo(drinks), [drinks]);

  const copyList = async () => {
    const lines = [
      "Trailhead Taps — Keg Shopping List",
      `Event: ${guests} guests · ${hours} hours${buffer ? " (with 10% buffer)" : ""}`,
      `Total drinks needed: ~${drinks} (12oz pours)`,
      "",
      "Recommended kegs:",
      ...combo.items.map((it) => `  • ${it.qty} × ${it.name}  (~${it.qty * it.pours} pours, ~$${it.qty * it.priceLow}–$${it.qty * it.priceHigh})`),
      "",
      `Estimated total beer cost: $${combo.low}–$${combo.high}`,
      "",
      "Where to buy near Long Beach:",
      "  • BevMo!  https://www.bevmo.com/stores" + UTM,
      "  • Total Wine & More  https://www.totalwine.com/store-finder?searchTerm=Long%20Beach%2C%20CA" + UTM.replace('?', '&'),
      "  • Costco (members only)  https://www.costco.com/beer.html" + UTM,
      "  • Local LB breweries (Beachwood, Liberation, ISM, Steady)",
    ].join("\n");
    try {
      await navigator.clipboard.writeText(lines);
      toast.success("Shopping list copied — paste it into your notes app!");
    } catch {
      toast.error("Couldn't copy — try selecting & copying manually.");
    }
  };

  return (
    <div className="bg-[#1f2e1f] text-[#e8dcc4] p-6 md:p-8" data-testid="keg-recommender">
      <div className="flex items-center gap-2 mb-4">
        <ShoppingCart className="h-4 w-4 text-[#f5b85c]" />
        <span className="text-xs uppercase tracking-[0.3em] text-[#f5b85c] font-semibold">Beer Shopping Calculator</span>
      </div>
      <h3 className="font-display text-3xl text-[#F8F5F0] mb-1">How many kegs do you actually need?</h3>
      <p className="text-sm text-[#e8dcc4]/70 mb-6">Tell us the size and we'll give you a smart keg combo + a budget range to take to the store.</p>

      <div className="space-y-5 mb-6">
        {/* Guests */}
        <div data-testid="kr-guests">
          <div className="flex items-baseline justify-between mb-2">
            <Label className="flex items-center gap-2 text-sm font-semibold text-[#e8dcc4]"><Users className="h-4 w-4 text-[#f5b85c]" /> Guests</Label>
            <span className="font-display text-2xl text-[#F8F5F0]">{guests}</span>
          </div>
          <Slider value={[guests]} min={5} max={300} step={5} onValueChange={(v) => setGuests(v[0])} className="[&_[role=slider]]:bg-[#f5b85c] [&_[role=slider]]:border-[#f5b85c] [&>span:first-child]:bg-[#e8dcc4]/15 [&_.bg-primary]:bg-[#f5b85c]" />
        </div>
        {/* Hours */}
        <div data-testid="kr-hours">
          <div className="flex items-baseline justify-between mb-2">
            <Label className="flex items-center gap-2 text-sm font-semibold text-[#e8dcc4]"><Clock className="h-4 w-4 text-[#f5b85c]" /> Service hours</Label>
            <span className="font-display text-2xl text-[#F8F5F0]">{hours}</span>
          </div>
          <Slider value={[hours]} min={2} max={8} step={1} onValueChange={(v) => setHours(v[0])} className="[&_[role=slider]]:bg-[#f5b85c] [&_[role=slider]]:border-[#f5b85c] [&>span:first-child]:bg-[#e8dcc4]/15 [&_.bg-primary]:bg-[#f5b85c]" />
        </div>
        {/* Buffer */}
        <div className="flex items-center justify-between border border-[#e8dcc4]/15 px-4 py-2.5">
          <div>
            <Label htmlFor="buffer-sw" className="text-sm font-semibold text-[#e8dcc4] cursor-pointer">Add 10% buffer</Label>
            <p className="text-[11px] text-[#e8dcc4]/55">Recommended — never run dry</p>
          </div>
          <Switch id="buffer-sw" checked={buffer} onCheckedChange={setBuffer} data-testid="kr-buffer" className="data-[state=checked]:bg-[#c97b3a]" />
        </div>
      </div>

      {/* OUTPUT */}
      <div className="border-t border-[#e8dcc4]/15 pt-6">
        <div className="flex items-baseline justify-between mb-4">
          <span className="text-xs uppercase tracking-[0.3em] text-[#e8dcc4]/60">Drinks needed</span>
          <span className="font-display text-4xl text-[#F8F5F0]" data-testid="kr-drinks">~{drinks}</span>
        </div>
        <div className="space-y-2 mb-5">
          {combo.items.map((it, i) => (
            <div key={i} data-testid={`kr-keg-${it.id}`} className="flex items-center justify-between bg-[#243024] border border-[#e8dcc4]/10 px-4 py-2.5">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-[#c97b3a]/15 flex items-center justify-center">
                  <Beer className="h-4 w-4 text-[#f5b85c]" />
                </div>
                <div>
                  <div className="font-display text-lg text-[#F8F5F0] leading-tight">{it.qty} × {it.name}</div>
                  <div className="text-[11px] uppercase tracking-wider text-[#e8dcc4]/55">{it.qty * it.pours} pours</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-[#f5b85c] font-medium">${it.qty * it.priceLow}–${it.qty * it.priceHigh}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between border-t border-[#e8dcc4]/15 pt-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#e8dcc4]/60 mb-1">Estimated beer budget</div>
            <div className="font-display text-3xl text-[#F8F5F0]" data-testid="kr-cost">${combo.low}–${combo.high}</div>
          </div>
          <button onClick={copyList} data-testid="kr-copy"
            className="bg-[#c97b3a] hover:bg-[#d68847] text-white px-4 py-3 text-sm font-semibold flex items-center gap-2 transition-colors">
            <Check className="h-4 w-4" /> Copy list
          </button>
        </div>
        <p className="text-[11px] text-[#e8dcc4]/55 mt-4 italic leading-relaxed">
          Math: ~2 drinks/guest in hour 1, then 1/guest each additional hour{buffer ? " · +10% safety buffer" : ""}. Prices are typical retail at BevMo / Total Wine for popular domestics + craft beers. Specialty/imports run higher.
        </p>
      </div>
    </div>
  );
}

export default function BringYourOwnBooze() {
  return (
    <section id="byo" className="bg-[#e8dcc4] border-t border-[#c9bfa6] scroll-mt-32">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-8 mb-10 items-end">
          <div className="lg:col-span-7">
            <span className="text-xs font-semibold uppercase tracking-[0.35em] text-[#c97b3a]">004 — Bring your own booze</span>
            <h2 className="font-display text-5xl md:text-7xl text-[#1f2e1f] leading-[0.9] mt-3">
              You bring the kegs.<br />
              <span className="italic text-[#c97b3a]">We bring the pour.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-[#1f2e1f] text-[#e8dcc4] p-6 flex items-start gap-4" data-testid="alcohol-disclaimer">
              <AlertCircle className="h-6 w-6 text-[#f5b85c] shrink-0 mt-0.5" />
              <div>
                <div className="font-display text-xl text-[#F8F5F0] leading-tight">Alcohol is <span className="italic text-[#f5b85c]">not</span> included in our rental pricing.</div>
                <p className="text-sm text-[#e8dcc4]/75 mt-2 leading-relaxed">California licensing means clients must supply their own kegs, wine, and spirits. We're glad to help you size quantities and source from local LB shops & breweries.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Store links */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {STORES.map((s, i) => (
            <a key={i} href={s.href} target="_blank" rel="noopener noreferrer sponsored"
              data-testid={`store-link-${i}`}
              className="group bg-white border border-[#c9bfa6] p-6 hover:-translate-y-1 hover:border-[#c97b3a] transition-all duration-300 flex flex-col">
              <div className="flex items-start justify-between mb-3">
                <MapPin className="h-5 w-5 text-[#c97b3a]" />
                <ExternalLink className="h-4 w-4 text-[#8c8577] group-hover:text-[#c97b3a] transition-colors" />
              </div>
              <h3 className="font-display text-2xl text-[#1f2e1f] leading-tight">{s.name}</h3>
              <p className="text-sm text-[#5a5245] mt-2 leading-snug">{s.tagline}</p>
              <p className="text-[11px] uppercase tracking-wider text-[#8c8577] mt-3">{s.note}</p>
              {s.mapsHref && (
                <span
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(s.mapsHref, '_blank', 'noopener,noreferrer'); }}
                  className="mt-auto pt-4 text-[11px] uppercase tracking-wider text-[#c97b3a] hover:underline cursor-pointer inline-flex items-center gap-1"
                  data-testid={`store-maps-${i}`}>
                  Find nearest <MapPin className="h-3 w-3" />
                </span>
              )}
            </a>
          ))}
        </div>

        {/* Keg sizes diagram */}
        <div className="grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="bg-white border border-[#c9bfa6] p-4 md:p-8">
              <img
                src={KEG_DIAGRAM}
                alt="Keg sizes reference: 1/2 Barrel, Slim 1/4, 1/4 Barrel, 1/6 Barrel, Corny Keg, The Greg — gallons and 12oz pour counts"
                className="w-full h-auto"
                data-testid="keg-diagram"
              />
            </div>
            <p className="text-xs text-[#8c8577] mt-3 italic">Diagram: each tap on the trailer can run any of the kegs above — mix & match across all 4 lines.</p>
          </div>

          <div className="lg:col-span-5">
            <KegRecommender />
          </div>
        </div>
      </div>
    </section>
  );
}
